import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { environment } from 'src/environments/environment';
import { Globals } from '../globals';
import { User } from '../models/user.model';
import { CardSurcharge } from '../models/card-surcharge.model';

const STORAGE_KEY_AUTH_ACCESS_TOKEN = "auth.accessToken";
const STORAGE_KEY_AUTH_ACCESS_TOKEN_EXPIRY = "auth.accessTokenExpiry";
const STORAGE_KEY_AUTH_REFRESH_TOKEN = 'auth.refreshToken'
const STORAGE_KEY_AUTH_SCOPES = 'auth.scopes'
const STORAGE_KEY_AUTH_USER_ID = 'auth.userId';
const STORAGE_KEY_AUTH_ENVIRONMENT = 'auth.environment';
const STORAGE_KEY_AUTH_CURRENT_USER = 'auth.currentUser';
const STORAGE_KEY_APP_PICKUP_LOCATIONS = 'app.pickupLocations';
const STORAGE_KEY_PAYMENT_CACHED = 'payment.cached';
const STORAGE_KEY_ENVIRONMENT_BASE_URL = 'env.baseUrl';

export interface CachedPayment {
  cart?: any;
  paymentRequestBody?: any;
  apiResponse?: any;
  shouldCardBeSaved?: boolean;
  isCreditAdjustment?: boolean;
  savedCardSurcharge?: CardSurcharge;
}

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private _accessToken: string;
  private _accessTokenExpiry: Date;
  private _refreshToken: string;
  private _baseUrl: string;
  private _scopes: string[];
  private _cachedPaymentApiResponse;

  userId: string;
  environment: string;

  currentUser: User;

  constructor(private storage: Storage, private globals: Globals) { }

  isAuthenticated(scope = null) {
    if (!this._accessToken) {
      console.log('No access token.');
      return false;
    } else if (this.isTokenExpired()) {
      console.log('Access token has expired.');
      return false;
    } else if (!this.isScoped(scope)) {
      console.log(`User does not have access to scope ${scope}.`);
      return false;
    }

    console.log('User has previous session still active.');
    return true;
  }

  // Convenience method that skips token validation steps already checked by guard
  isScoped(scope) {
    return !scope || this._scopes.includes(scope)
  }


  isTokenExpired(): boolean {
    var now: number = new Date().getTime();
    var accessTokenExpiry: number = new Date(this._accessTokenExpiry).getTime();
    return accessTokenExpiry <= now;
  }

  public getBaseUrl(): string {
    return this._baseUrl;
  }

  public async saveBaseUrl(baseUrl): Promise<void> {
    this._baseUrl = baseUrl;
    await this.storage.set(STORAGE_KEY_ENVIRONMENT_BASE_URL, baseUrl);
  }

  get accessToken() {
    return this._accessToken;
  }

  async retrieveFromStorage() {
    this._accessToken = await this.storage.get(STORAGE_KEY_AUTH_ACCESS_TOKEN);
    this._accessTokenExpiry = await this.storage.get(STORAGE_KEY_AUTH_ACCESS_TOKEN_EXPIRY);
    this._refreshToken = await this.storage.get(STORAGE_KEY_AUTH_REFRESH_TOKEN);
    this._scopes = await this.storage.get(STORAGE_KEY_AUTH_SCOPES);
    this.userId = await this.storage.get(STORAGE_KEY_AUTH_USER_ID);
    this._baseUrl = await this.storage.get(STORAGE_KEY_ENVIRONMENT_BASE_URL);
    this.environment = await this.storage.get(STORAGE_KEY_AUTH_ENVIRONMENT);
    console.log('Loaded session data.', {
      userId: this.userId,
      environment: this.environment,
      scopes: this._scopes
    });
    await this.getCachedPayment();
  }

  async getCachedPayment() {
    try {
      let jsonStringFromStorage = await this.storage.get(STORAGE_KEY_PAYMENT_CACHED ?? '');
      this._cachedPaymentApiResponse = JSON.parse(jsonStringFromStorage);
    } catch (e) {
      console.log("Error retrieving cached payment API response.");
      this._cachedPaymentApiResponse = {};
    }
    return this._cachedPaymentApiResponse ?? {};
  }

  async setCachedPayment(cachedPayment: CachedPayment) {
    await this.storage.set(STORAGE_KEY_PAYMENT_CACHED, JSON.stringify(cachedPayment));
    return;
  }

  async removeCachedPayment() {
    await this.storage.remove(STORAGE_KEY_PAYMENT_CACHED);
    return;
  }

  async saveCredentials(accessToken, accessTokenExpiry, refreshToken, scopes, username, environment) {
    await this.storage.set(STORAGE_KEY_AUTH_ACCESS_TOKEN, accessToken);
    await this.storage.set(STORAGE_KEY_AUTH_ACCESS_TOKEN_EXPIRY, accessTokenExpiry);
    await this.storage.set(STORAGE_KEY_AUTH_REFRESH_TOKEN, refreshToken);
    await this.storage.set(STORAGE_KEY_AUTH_SCOPES, scopes);
    await this.storage.set(STORAGE_KEY_AUTH_USER_ID, username);
    await this.storage.set(STORAGE_KEY_AUTH_ENVIRONMENT, environment);
    await this.retrieveFromStorage();
  }


  setCurrentStoredUser(currentUser: User) {
    this.storage.set(STORAGE_KEY_AUTH_CURRENT_USER, currentUser);
    this.getCurrentStoredUser;
  }

  async getCurrentStoredUser() {
    this.currentUser = (await this.storage.get(STORAGE_KEY_AUTH_CURRENT_USER));
    return this.currentUser;
  }


  setPickupLocations(locations: any[]) {
    this.storage.set(STORAGE_KEY_APP_PICKUP_LOCATIONS, locations);
  }

  getPickupLocations() {
    return this.storage.get(STORAGE_KEY_APP_PICKUP_LOCATIONS);
  }

  async clearSession() {
    // await this.storage.remove('auth.environment');
    await this.storage.clear();
    this.retrieveFromStorage();
  }

}
